import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPause,
  faPlay,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import {
  checkBPValues,
  checkPulseValue,
  dedupeMeasurementsByDateTimeKeepLatest,
  formatMeasurementTableDate,
  formatMeasurementTableTime,
  getMeasurementValueClass,
  getMeasurementWallClockDateYmd,
  getMeasurementWallClockTimeHms,
  parseWallClockDateLocal
} from '../../../utils/measurementUtils';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import AddBPForm from './AddBPForm';
import { toast } from 'react-toastify';
import VitalsTabs from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EmailModal from '../../../components/EmailModal/EmailModal';
import { sendReportEmail } from '../../../utils/reportEmailSend';
import { printElement, downloadBlob, toCsv } from '../../../utils/reportExportUtils';
import { exportBloodPressureTablesPdf } from '../../../utils/measurementTablePdfExport';
import {
  findPeriodByName,
  hasAnyScheduleTargets,
  normalizePeriodName,
  resolvePeriodNameByIdOrLabel,
  resolveDisplayScheduleTargets,
  getMeasurementEpochMs
} from '../../../utils/scheduleTargetUtils';
import {
  PATIENT_SCHEDULE_TARGET_PREF_EVENT,
  getUsePatientScheduleTargetsAtMeasurementMs,
  getUsePatientScheduleTargetsPreference
} from '../../../utils/patientScheduleTargetPreference.js';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import {
  canAddPracticeVital,
  MEASURED_TYPE_IDS,
  normalizePracticeMonitoringTypes,
  PRACTICE_VITAL_ADD_DISABLED_TITLE
} from '../../../utils/practiceMonitoringUtils';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import {
  DEFAULT_MEASUREMENT_QUERY,
  applyMeasurementFromDateChange,
  getMeasurementQueryMaxDate,
  isFromToIncomplete,
  isMeasurementQueryDirty,
  patchOnQueryModeChange,
  sanitizeMeasurementToDate,
} from '../../../utils/queryPeriodFilters';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import PulseIcon from '../../../components/Common/PulseIcon/PulseIcon';
import pulseIcon from '../../../assets/pulse.png';
import { useMeasurementSessionOptional } from '../MeasurementControlsBar';
import {
  PERMANENT_END_TIMES,
  PERMANENT_START_TIMES,
  determinePeriodFromTime,
  getPermanentPeriodDisplay
} from '../../../utils/measurementPeriodUtils';
import '../MeasurementShell.css';
import './BloodPressure.css';

const BloodPressure = ({ practiceId, patientId, practiceIsLocked = false, practiceMonitoringTypes = null }) => {
  const { updateBreadcrumbs } = useBreadcrumb();
  const location = useLocation();
  const session = useMeasurementSessionOptional();
  const useSharedControls = Boolean(session);

  const [localTimer, setLocalTimer] = useState(0);
  const [localIsRunning, setLocalIsRunning] = useState(false);
  const timer = useSharedControls ? session.timer : localTimer;
  const setTimer = useSharedControls ? session.setTimer : setLocalTimer;
  const isRunning = useSharedControls ? session.isRunning : localIsRunning;
  const setIsRunning = useSharedControls ? session.setIsRunning : setLocalIsRunning;
  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const [sortBy, setSortBy] = useState(DEFAULT_MEASUREMENT_QUERY.sortBy);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [targetTimelineRev, setTargetTimelineRev] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(true);
  const [hasFetchedPatientData, setHasFetchedPatientData] = useState(false);
  const [hasFetchedScheduleTargets, setHasFetchedScheduleTargets] = useState(false);
  const [hasFetchedMeasurements, setHasFetchedMeasurements] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null); // { x, y, readings, title, periodName }
  const initialized = useRef(false);
  const hoverHideTimerRef = useRef(null);
  const bpTimePeriods = useMemo(
    () => (Array.isArray(scheduleTargets?.bloodPressureTimePeriod) ? scheduleTargets.bloodPressureTimePeriod : []),
    [scheduleTargets]
  );
  const bpTargetRanges = useMemo(
    () => (Array.isArray(scheduleTargets?.bloodPressureTargetRange) ? scheduleTargets.bloodPressureTargetRange : []),
    [scheduleTargets]
  );

  const resolveTargetsBundleAtMs = useCallback(
    (ms) => {
      if (!scheduleTargetSources) return null;
      const usePatient = getUsePatientScheduleTargetsAtMeasurementMs(practiceId, patientId, ms);
      return resolveDisplayScheduleTargets({
        practiceData: scheduleTargetSources.practiceData,
        patientData: scheduleTargetSources.patientData,
        usePatientTargets: usePatient
      });
    },
    [scheduleTargetSources, practiceId, patientId, targetTimelineRev]
  );

  useEffect(() => {
    if (practiceIsLocked) {
      setShowAddModal(false);
      setShowFollowUpModal(false);
      if (!useSharedControls) {
        setIsRunning(false);
        setTimer(0);
      }
    }
  }, [practiceIsLocked, useSharedControls]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setHasFetchedPatientData(false);
      setHasFetchedScheduleTargets(false);
      setHasFetchedMeasurements(false);
      setScheduleTargetSources(null);
      if (!initialized.current) {
        if (!practiceIsLocked && !useSharedControls) {
          setIsRunning(true);
        }
        initialized.current = true;
      }
      fetchPatientData();
      fetchScheduleTargets();
    }
    return () => {
      initialized.current = false;
    };
  }, [practiceId, patientId]);

  // Fetch measurements on initial load / patient change only (filters apply via Query button).
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements({ silent: true });
    }
  }, [practiceId, patientId]);

  // Timer effect (local only — shared timer lives in MeasurementControlsBar)
  useEffect(() => {
    if (useSharedControls) return undefined;
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, useSharedControls]);

  const fetchPatientData = async () => {
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') {
          setPatientData(cached);
        }
      } catch {
        // ignore cache parse issues
      }
    }
    try {
      const response = await patientService.getPatientDetailsFast(practiceId, patientId);
      if (response.data.success) {
        const next = response.data.data;
        setPatientData(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setHasFetchedPatientData(true);
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      setScheduleTargetSources(sources);
      const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId);
      const effective = resolveDisplayScheduleTargets({
        practiceData: sources.practiceData,
        patientData: sources.patientData,
        usePatientTargets
      });
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    } finally {
      setHasFetchedScheduleTargets(true);
    }
  };

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setTargetTimelineRev((n) => n + 1);
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  const fetchMeasurements = async ({ silent = false } = {}) => {
    try {
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          fromDateParam = startDate.toISOString().split('T')[0];
          toDateParam = endDate.toISOString().split('T')[0];
        }
      } else if (fromDate && toDate) {
        fromDateParam = fromDate;
        toDateParam = toDate;
      }

      if (!isAllHistory && !(fromDateParam && toDateParam)) {
        return;
      }

      const queryCacheKey = `measurement:bp:${practiceId}:${patientId}:${queryType}:${dateRange}:${fromDateParam || ''}:${toDateParam || ''}:${sortBy}`;
      let hadCachedData = false;
      const cachedRaw = sessionStorage.getItem(queryCacheKey);
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw);
          if (Array.isArray(cached)) {
            setMeasurements(cached);
            setHasFetchedMeasurements(true);
            hadCachedData = true;
          }
        } catch {
          // ignore cache parse issues
        }
      }
      if (!silent || !hadCachedData) {
        setQueryLoading(true);
      }

      const response = await measurementService.getBloodPressureByDateRange(
        practiceId,
        patientId,
        isAllHistory ? undefined : fromDateParam,
        isAllHistory ? undefined : toDateParam,
        sortBy.toLowerCase().replace(' ', '_')
      );

      if (response.data?.success) {
        const next = response.data.data || [];
        setMeasurements((prev) => (JSON.stringify(prev || []) === JSON.stringify(next) ? prev : next));
        sessionStorage.setItem(queryCacheKey, JSON.stringify(next));
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);

      // Handle 404 gracefully - API endpoint may not be implemented yet
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
        // Don't show error toast for 404 - endpoint may not be implemented yet
      } else {
        // Only show error for other types of errors
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
      setHasFetchedMeasurements(true);
    }
  };

  const isQueryDisabled = queryLoading || isFromToIncomplete(queryType, fromDate, toDate);
  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy,
  });

  const handleQueryTypeChange = (nextType) => {
    const patch = patchOnQueryModeChange(nextType);
    setQueryType(patch.queryType);
    if (Object.prototype.hasOwnProperty.call(patch, 'fromDate')) {
      setFromDate(patch.fromDate);
      setToDate(patch.toDate);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements({ silent: false });
  };

  const handleReset = () => {
    if (isResetDisabled) return;
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
    setSortBy(DEFAULT_MEASUREMENT_QUERY.sortBy);
  };

  const handleAddDataSuccess = () => {
    fetchMeasurements({ silent: false });
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')} : ${secs.toString().padStart(2, '0')}`;
  };

  // Wall-clock only — never new Date() on measurement stamps (USA TZ day/hour shift).
  const formatDate = (dateString) => formatMeasurementTableDate(dateString);
  const formatTime = (timeString) => formatMeasurementTableTime(timeString);

  const resolvePeriodByTime = (timeString, periods) =>
    determinePeriodFromTime(
      timeString,
      periods,
      PERMANENT_START_TIMES.bloodPressure,
      PERMANENT_END_TIMES.bloodPressure
    ) || '';

  // Get patient info
  const patient = patientData?.patient;
  const patientInfo = useMemo(() => {
    const patientName = patient ? `${patient.last_name || ''}, ${patient.first_name || ''}`.trim() : 'Loading...';
    const dobRaw = patient?.date_of_birth || patient?.dob || patient?.patient_birthday || '';
    const dobDate = dobRaw ? new Date(dobRaw) : null;
    const hasValidDob = dobDate && !Number.isNaN(dobDate.getTime());
    const patientDOB = hasValidDob
      ? `${formatDate(dobRaw)} (${new Date().getFullYear() - dobDate.getFullYear()} years)`
      : 'N/A';
    const patientPhone = formatPhoneForDisplay(patient?.phone_number, 'N/A');
    const patientDevice = patientData?.device_serial || 'N/A';
    return { patientName, patientDOB, patientPhone, patientDevice };
  }, [patient, patientData?.device_serial]);
  const { patientName, patientDOB, patientPhone, patientDevice } = patientInfo;
  const patientVitals = patientData?.patient?.vitals || '';
  const resolvedPracticeTypes = useMemo(() => {
    if (Array.isArray(practiceMonitoringTypes)) return practiceMonitoringTypes;
    return normalizePracticeMonitoringTypes(patientData?.practice_monitoring_types);
  }, [practiceMonitoringTypes, patientData?.practice_monitoring_types]);
  const canAddThisVital = canAddPracticeVital({
    practiceIsLocked,
    practiceMonitoringTypes: resolvedPracticeTypes,
    typeId: MEASURED_TYPE_IDS.BP
  });

  useEffect(() => {
    if (!canAddThisVital) setShowAddModal(false);
  }, [canAddThisVital]);

  // Set breadcrumbs (must be after patientName is declared)
  useEffect(() => {
    if (patientData) {
      const fromDashboard = location.state?.fromDashboard;
      const rootCrumb = { label: 'Dashboard', path: '/dashboard' };
      const basePath = fromDashboard ? '/dashboard' : '/practices';

      updateBreadcrumbs([
        rootCrumb,
        { label: patientData?.patient?.practice_name || patientData?.practice?.practice_name || 'Practice', path: `${basePath}/${practiceId}`, state: { fromDashboard } },
        { label: 'Patients', path: `${basePath}/${practiceId}?tab=patient`, state: { fromDashboard } },

        { label: patientName, path: `/practices/${practiceId}/patients/${patientId}`, state: { fromDashboard } },
        { label: 'Summary Of Measurement', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, patientName, updateBreadcrumbs, location.state]);


  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    if (bpTimePeriods.length === 0) return [];
    const seen = new Set();
    return bpTimePeriods
      .map((p) => p?.name)
      .filter(Boolean)
      .filter((name) => {
        const key = normalizePeriodName(name);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [bpTimePeriods]);

  const getPeriodColor = () => '#F8FAFC';

  // Get period info (including time ranges) from schedule targets
  const getPeriodInfo = (periodName) => {
    if (bpTimePeriods.length === 0) {
      return null;
    }

    const period = findPeriodByName(bpTimePeriods, periodName);

    if (!period) return null;

    const display = getPermanentPeriodDisplay(
      periodName,
      PERMANENT_START_TIMES.bloodPressure,
      PERMANENT_END_TIMES.bloodPressure,
      period
    );

    return {
      name: period.name,
      startTime: display.startTime,
      endTime: display.endTime,
      startTimeDisplay: display.startTimeDisplay,
      endTimeDisplay: display.endTimeDisplay
    };
  };

  const getResolvedPeriodName = (measurement) => {
    const ms = getMeasurementEpochMs(measurement);
    const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
    const periodsForRow = Array.isArray(bundle?.bloodPressureTimePeriod) ? bundle.bloodPressureTimePeriod : bpTimePeriods;

    const mTime = getMeasurementWallClockTimeHms(measurement);
    if (mTime) {
      const timeResolved = resolvePeriodByTime(mTime, periodsForRow);
      if (timeResolved) return timeResolved;
    }

    const rawLabel =
      measurement?.period_name ||
      measurement?.period ||
      measurement?.measure_note ||
      measurement?.note ||
      '';

    const fromConfigured = rawLabel
      ? resolvePeriodNameByIdOrLabel(periodsForRow, { ...measurement, period_name: rawLabel })
      : resolvePeriodNameByIdOrLabel(periodsForRow, measurement);
    if (fromConfigured) return fromConfigured;

    if (rawLabel) {
      const normalizedRaw = normalizePeriodName(rawLabel);
      const direct = periodNames.find((p) => normalizePeriodName(p) === normalizedRaw);
      if (direct) return direct;
      return rawLabel;
    }

    return 'Unknown';
  };

  // Get target ranges for each period (optional bundle for historical categorization)
  const getTargetForPeriod = (periodName, bundleOverride) => {
    const bundle = bundleOverride || scheduleTargets;
    const ranges = Array.isArray(bundle?.bloodPressureTargetRange) ? bundle.bloodPressureTargetRange : [];
    const periods = Array.isArray(bundle?.bloodPressureTimePeriod) ? bundle.bloodPressureTimePeriod : [];
    const pickFirstNumber = (...values) => {
      for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return null;
    };
    const normalizeTargetKey = (value) =>
      normalizePeriodName(value).replace(/targetranges?$/, '').replace(/targets?$/, '');

    if (ranges.length === 0) {
      return {
        systolicMin: 100,
        systolicMax: 140,
        diastolicMin: 60,
        diastolicMax: 90,
        pulseMin: 60,
        pulseMax: 100
      };
    }
    const normalizedInput = normalizeTargetKey(periodName);
    const period = periods.find(
      (p) => normalizeTargetKey(p.name) === normalizedInput
    );
    const target = ranges.find((t) => {
      const targetKey = normalizeTargetKey(t.name);
      return (
        targetKey === normalizedInput ||
        targetKey.includes(normalizedInput) ||
        normalizedInput.includes(targetKey) ||
        t.id === period?.id
      );
    }) || ranges[0];

    return {
      systolicMin: pickFirstNumber(target.systolicMin, target.sysMin, target.systolic_min, target.systolic_pressure_min) ?? 100,
      systolicMax: pickFirstNumber(target.systolicMax, target.sysMax, target.systolic_max, target.systolic_pressure_max) ?? 140,
      diastolicMin: pickFirstNumber(target.diastolicMin, target.diaMin, target.diastolic_min, target.diastolic_pressure_min) ?? 60,
      diastolicMax: pickFirstNumber(target.diastolicMax, target.diaMax, target.diastolic_max, target.diastolic_pressure_max) ?? 90,
      pulseMin: pickFirstNumber(target.pulseMin, target.pulse_min, target.minPulse) ?? 60,
      pulseMax: pickFirstNumber(target.pulseMax, target.pulse_max, target.maxPulse) ?? 100
    };
  };

  const checkPulseStatus = (pulseValue, target) => {
    return checkPulseValue(pulseValue, target);
  };

  // Group measurements by date - keep ALL measurements
  const effectiveMeasurements = useMemo(
    () => dedupeMeasurementsByDateTimeKeepLatest(measurements),
    [measurements]
  );

  // Group measurements by date
  const groupedMeasurements = useMemo(() => {
    const grouped = {};

    // Sort all measurements by date and time (newest first)
    const sortedMeasurements = [...effectiveMeasurements].sort((a, b) => {
      const dateA = getMeasurementWallClockDateYmd(a);
      const dateB = getMeasurementWallClockDateYmd(b);

      if (dateA !== dateB) {
        return dateB.localeCompare(dateA); // Descending by date (YYYY-MM-DD)
      }

      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA); // Descending by time
    });

    // 3. Group by date - each measurement is stored in a flat array per date
    sortedMeasurements.forEach(measurement => {
      const date = getMeasurementWallClockDateYmd(measurement);
      if (!date) return;

      if (!grouped[date]) {
        grouped[date] = [];
      }

      grouped[date].push(measurement);
    });

    return grouped;
  }, [effectiveMeasurements]);

  // Get all unique dates sorted
  const sortedDates = useMemo(() => {
    return Object.keys(groupedMeasurements).sort((a, b) => b.localeCompare(a));
  }, [groupedMeasurements]);

  const groupedByWeek = useMemo(() => {
    if (measurements.length === 0) return [];
    
    const weeksMap = {};
    measurements.forEach(m => {
      const dateStr = getMeasurementWallClockDateYmd(m);
      if (!dateStr) return;
      
      const d = parseWallClockDateLocal(dateStr);
      if (!d) return;
      // Start of week (Sunday)
      const sun = new Date(d);
      sun.setDate(d.getDate() - d.getDay());
      sun.setHours(0, 0, 0, 0);
      const weekKey = `${sun.getFullYear()}-${String(sun.getMonth() + 1).padStart(2, '0')}-${String(sun.getDate()).padStart(2, '0')}`;
      
      if (!weeksMap[weekKey]) {
        const sat = new Date(sun);
        sat.setDate(sun.getDate() + 6);
        
        weeksMap[weekKey] = {
          key: weekKey,
          start: sun,
          end: sat,
          label: `${formatDate(sun)} ~ ${formatDate(sat)}`,
          dates: {},
          allMeasurements: []
        };
      }
      
      if (!weeksMap[weekKey].dates[dateStr]) {
        weeksMap[weekKey].dates[dateStr] = [];
      }
      weeksMap[weekKey].dates[dateStr].push(m);
      weeksMap[weekKey].allMeasurements.push(m);
    });

    return Object.keys(weeksMap)
      .sort((a, b) => new Date(b) - new Date(a))
      .map(key => {
        const week = weeksMap[key];
        return {
          ...week,
          sortedDates: Object.keys(week.dates).sort((a, b) => new Date(b) - new Date(a))
        };
      });
  }, [measurements]);

  // Calculate statistics for a period - uses ALL measurements across all dates
  const calculatePeriodStats = (allMeasurements, periodName) => {
    // Filter measurements for this specific period
    const normalizedPeriod = normalizePeriodName(periodName);
    const periodMeasurements = allMeasurements.filter(m => {
      const period = getResolvedPeriodName(m);
      return normalizePeriodName(period) === normalizedPeriod;
    });

    if (!periodMeasurements || periodMeasurements.length === 0) {
      return { avg: null, max: null, min: null, count: 0, above: 0, below: 0, within: 0 };
    }

    const sysValues = periodMeasurements.map(m => parseFloat(m.systolic_pressure)).filter(v => !isNaN(v));
    const diaValues = periodMeasurements.map(m => parseFloat(m.diastolic_pressure)).filter(v => !isNaN(v));
    const pulseValues = periodMeasurements.map(m => {
      const pulse = m.pulse;
      // Ensure pulse is a number, not a string
      if (pulse == null || pulse === undefined) return null;
      return typeof pulse === 'string' ? parseFloat(pulse) : pulse;
    }).filter(v => v != null && !isNaN(v));
    const target = getTargetForPeriod(periodName);

    const sysAvg = sysValues.length > 0 ? sysValues.reduce((a, b) => a + b, 0) / sysValues.length : null;
    const diaAvg = diaValues.length > 0 ? diaValues.reduce((a, b) => a + b, 0) / diaValues.length : null;
    const pulseAvg = pulseValues.length > 0 ? pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length : null;
    const sysMax = sysValues.length > 0 ? Math.max(...sysValues) : null;
    const diaMax = diaValues.length > 0 ? Math.max(...diaValues) : null;
    const pulseMax = pulseValues.length > 0 ? Math.max(...pulseValues) : null;
    const sysMin = sysValues.length > 0 ? Math.min(...sysValues) : null;
    const diaMin = diaValues.length > 0 ? Math.min(...diaValues) : null;
    const pulseMin = pulseValues.length > 0 ? Math.min(...pulseValues) : null;

    let above = 0, below = 0, within = 0;
    periodMeasurements.forEach(m => {
      const { sysStatus, diaStatus } = checkBPValues(m, target);
      if (sysStatus === 'high' || diaStatus === 'high') above++;
      else if (sysStatus === 'low' || diaStatus === 'low') below++;
      else within++;
    });

    return {
      avg: { sys: sysAvg, dia: diaAvg, pulse: pulseAvg },
      max: { sys: sysMax, dia: diaMax, pulse: pulseMax },
      min: { sys: sysMin, dia: diaMin, pulse: pulseMin },
      count: periodMeasurements.length,
      above,
      below,
      within
    };
  };

  // Render all readings for one period/date inside the same table cells
  const renderPeriodMeasurementsCells = (periodMeasurements, periodName) => {
    if (!periodMeasurements || periodMeasurements.length === 0) {
      return (
        <>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </>
      );
    }

    const target = getTargetForPeriod(periodName);
    const readings = [...periodMeasurements].sort((a, b) => {
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

    const renderPulseWithIcon = (value, status) => {
      if (value == null || value === '') return '';
      let cellClass = 'bp-value-normal';
      if (status === 'high') cellClass = 'bp-value-high';
      else if (status === 'low') cellClass = 'bp-value-low';
      const isAbnormal = status === 'high' || status === 'low';
      return (
        <span className={`bp-pulse-with-icon ${cellClass}`}>
          {Math.round(value)}
          <PulseIcon isAbnormal={isAbnormal} className="bp-pulse-icon" />
        </span>
      );
    };

    return (
      <>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const { sysStatus, diaStatus } = checkBPValues(measurement, target);
              let cellClass = 'bp-value-normal';
              if (sysStatus === 'high') cellClass = 'bp-value-high';
              else if (sysStatus === 'low') cellClass = 'bp-value-low';
              return (
                <div key={`${measurement.id || index}-sys`} className={cellClass}>
                  {measurement.systolic_pressure ? Math.round(measurement.systolic_pressure) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const { sysStatus, diaStatus } = checkBPValues(measurement, target);
              let cellClass = 'bp-value-normal';
              if (diaStatus === 'high') cellClass = 'bp-value-high';
              else if (diaStatus === 'low') cellClass = 'bp-value-low';
              return (
                <div key={`${measurement.id || index}-dia`} className={cellClass}>
                  {measurement.diastolic_pressure ? Math.round(measurement.diastolic_pressure) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const pulseValue = measurement.pulse
                ? (typeof measurement.pulse === 'string' ? parseFloat(measurement.pulse) : measurement.pulse)
                : null;
              const pulseStatus = checkPulseStatus(pulseValue, target);
              return (
                <div key={`${measurement.id || index}-pulse`}>
                  {renderPulseWithIcon(pulseValue, pulseStatus)}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const measureTime = getMeasurementWallClockTimeHms(measurement);
              const formattedTime = measureTime ? formatTime(measureTime) : '';
              return <div key={`${measurement.id || index}-time`} className="bp-time-text">{formattedTime}</div>;
            })}
          </div>
        </td>
      </>
    );
  };

  // Calculate totals across all periods - uses UNIQUE measurements
  const totals = useMemo(() => {
    let totalAboveSys = 0, totalAboveDia = 0;
    let totalBelowSys = 0, totalBelowDia = 0;
    let totalWithinSys = 0, totalWithinDia = 0;

    effectiveMeasurements.forEach(m => {
      const period = getResolvedPeriodName(m);
      const target = getTargetForPeriod(period);
      const { sysStatus, diaStatus } = checkBPValues(m, target);
      
      const sysVal = m.systolic_pressure;
      const diaVal = m.diastolic_pressure;

      if (sysVal != null && sysVal !== '') {
        if (sysStatus === 'high') totalAboveSys++;
        else if (sysStatus === 'low') totalBelowSys++;
        else totalWithinSys++;
      }

      if (diaVal != null && diaVal !== '') {
        if (diaStatus === 'high') totalAboveDia++;
        else if (diaStatus === 'low') totalBelowDia++;
        else totalWithinDia++;
      }
    });

    const sysTotal = totalAboveSys + totalBelowSys + totalWithinSys;
    const diaTotal = totalAboveDia + totalBelowDia + totalWithinDia;

    return {
      above: { sys: totalAboveSys, dia: totalAboveDia },
      below: { sys: totalBelowSys, dia: totalBelowDia },
      within: { sys: totalWithinSys, dia: totalWithinDia },
      sysTotal,
      diaTotal
    };
  }, [measurements, bpTimePeriods, bpTargetRanges]);

  const formatPercentCount = (count, total) => {
    if (!total) return `0% (${count})`;
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const periodList = useMemo(
    () => (periodNames.length > 0 ? periodNames : ['Wake-up', 'Morning', 'Noon', 'Afternoon', 'Evening']),
    [periodNames]
  );

  const getPeriodBadgeClass = (periodName) => {
    const n = normalizePeriodName(periodName);
    if (n.includes('wake')) return 'wakeup';
    if (n.includes('morning')) return 'morning';
    if (n.includes('noon') && !n.includes('after')) return 'noon';
    if (n.includes('afternoon')) return 'afternoon';
    if (n.includes('evening') || n.includes('night')) return 'evening';
    return 'other';
  };

  const getReadingTime = (measurement) => {
    const measureTime = getMeasurementWallClockTimeHms(measurement);
    return measureTime ? formatTime(measureTime) : '';
  };

  const sortReadingsNewestFirst = (arr) =>
    [...(arr || [])].sort((a, b) => {
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

  const getDatePeriodMap = (date) => {
    const dateMeasurements = groupedMeasurements[date] || [];
    const byPeriod = {};
    dateMeasurements.forEach((m) => {
      const resolvedPeriod = getResolvedPeriodName(m);
      const matchedPeriod = periodList.find(
        (p) => normalizePeriodName(p) === normalizePeriodName(resolvedPeriod)
      );
      if (!matchedPeriod) return;
      if (!byPeriod[matchedPeriod]) byPeriod[matchedPeriod] = [];
      byPeriod[matchedPeriod].push(m);
    });
    return byPeriod;
  };

  const getReadingLevelClass = (status) => {
    if (status === 'high') return 'high';
    if (status === 'low') return 'low';
    if (status === 'normal') return 'within';
    return '';
  };

  const getReadingBpStatuses = (measurement, periodName) => {
    const target = getTargetForPeriod(periodName);
    return checkBPValues(measurement, target);
  };

  const getBpColorAndLabel = (sys, dia, target) => {
    const s = Number(sys);
    const d = Number(dia);
    if (Number.isNaN(s) || Number.isNaN(d) || !sys || !dia) {
      return { color: '#3a9d3a', label: 'normal' };
    }
    if (s > 180 || d > 120) return { color: '#c0392b', label: 'crisis' };

    const sysMax = target?.systolic_pressure_max ?? 140;
    const diaMax = target?.diastolic_pressure_max ?? 90;
    const sysMin = target?.systolic_pressure_min ?? 90;
    const diaMin = target?.diastolic_pressure_min ?? 60;

    if (s >= sysMax || d >= diaMax) return { color: '#c2652e', label: 'high' };
    if (s < sysMin || d < diaMin) return { color: '#c0392b', label: 'low' };
    if (s >= 130 || d >= 80) return { color: '#c1900c', label: 'elevated' };
    return { color: '#3a9d3a', label: 'normal' };
  };

  const getReadingPulseStatus = (measurement, periodName) => {
    const target = getTargetForPeriod(periodName);
    const pulseValue =
      measurement?.pulse != null && measurement.pulse !== ''
        ? Number(measurement.pulse)
        : null;
    return checkPulseStatus(pulseValue, target);
  };

  const dateRangeLabel = useMemo(() => {
    if (!sortedDates.length) return '';
    const newest = sortedDates[0];
    const oldest = sortedDates[sortedDates.length - 1];
    if (newest === oldest) return formatDate(newest);
    return `${formatDate(oldest)} – ${formatDate(newest)}`;
  }, [sortedDates]);

  const clearHoverHideTimer = () => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  };

  const scheduleHoverHide = () => {
    clearHoverHideTimer();
    hoverHideTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
      hoverHideTimerRef.current = null;
    }, 180);
  };

  const showHoverPreviewAt = (event, readings, periodName) => {
    if (!readings || readings.length < 2) return;
    clearHoverHideTimer();
    const sorted = sortReadingsNewestFirst(readings);
    const others = sorted.slice(1);
    const sysVals = sorted
      .map((r) => Number(r.systolic_pressure))
      .filter((n) => !Number.isNaN(n));
    const diaVals = sorted
      .map((r) => Number(r.diastolic_pressure))
      .filter((n) => !Number.isNaN(n));
    const pulseVals = sorted
      .map((r) => Number(r.pulse))
      .filter((n) => !Number.isNaN(n) && n !== 0);
    const avgSys = sysVals.length
      ? Math.round(sysVals.reduce((a, b) => a + b, 0) / sysVals.length)
      : null;
    const avgDia = diaVals.length
      ? Math.round(diaVals.reduce((a, b) => a + b, 0) / diaVals.length)
      : null;
    const avgPulse = pulseVals.length
      ? Math.round(pulseVals.reduce((a, b) => a + b, 0) / pulseVals.length)
      : null;
    const averageLabel =
      avgSys != null && avgDia != null
        ? `${avgSys}/${avgDia}${avgPulse != null ? ` · ${avgPulse} bpm` : ''}`
        : '—';
    const x = event.clientX + 12;
    const y = event.clientY + 12;
    setHoverPreview({
      x: Math.min(x, window.innerWidth - 220),
      y: Math.min(y, window.innerHeight - 120),
      title: `${String(periodName || 'Period').toUpperCase()} • EARLIER READINGS`,
      periodName,
      totalCount: sorted.length,
      averageLabel,
      readings: others
    });
  };

  useEffect(() => () => clearHoverHideTimer(), []);

  const reportTitle = `Blood Pressure Report - ${patientName}`;
  const exportFilename = `BP-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;

  const handlePrint = () => {
    try {
      const el = document.getElementById('bp-report-content');
      printElement(el, reportTitle);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  useEffect(() => {
    if (!showExportMenu) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.bp-export-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExportSubmit = async (format) => {
    if (isExporting) return;
    setIsExporting(true);
    setShowExportMenu(false);

    if (format === 'pdf') {
      try {
        exportBloodPressureTablesPdf({
          filename: exportFilename,
          title: reportTitle,
          patient: {
            name: patientName,
            dob: patientDOB,
            phone: patientPhone,
            device: patientDevice
          },
          periodList,
          sortedDates,
          groupedMeasurements,
          getResolvedPeriodName,
          formatDate,
          formatTime,
          calculatePeriodStats,
          effectiveMeasurements,
          totals,
          formatPercentCount
        });
        toast.success('Exported to PDF');
        patientService.logPatientExport(practiceId, patientId, { format: 'pdf', source: 'Blood Pressure' });
      } catch (e) {
        toast.error(e?.message || 'Export failed');
      } finally {
        setIsExporting(false);
      }
    } else if (format === 'csv') {
      try {
        const rows = [];
        
        // Patient Info
        rows.push(['Patient Info']);
        rows.push(['Name', patientName]);
        rows.push(['DOB', patientDOB]);
        rows.push(['Phone', patientPhone]);
        rows.push(['Device', patientDevice]);
        rows.push([]);
        
        // Query Info
        rows.push(['Query Period']);
        rows.push(['Date Range', dateRangeLabel || 'All']);
        rows.push([]);
        
        // Readings
        rows.push(['Blood Pressure Measurements']);
        rows.push(['Date', 'Period', 'Systolic', 'Diastolic', 'Pulse', 'Time']);
        
        sortedDates.forEach((date) => {
          periodList.forEach((periodName) => {
            const byPeriod = getDatePeriodMap(date);
            const readings = sortReadingsNewestFirst(byPeriod[periodName] || []);
            readings.forEach((r) => {
              const sys = r.systolic_pressure != null ? Math.round(Number(r.systolic_pressure)) : '';
              const dia = r.diastolic_pressure != null ? Math.round(Number(r.diastolic_pressure)) : '';
              const pulseVal = r.pulse != null && r.pulse !== '' ? Math.round(Number(r.pulse)) : '';
              rows.push([
                formatDate(date),
                periodName,
                sys,
                dia,
                pulseVal,
                getReadingTime(r) || ''
              ]);
            });
          });
        });
        
        rows.push([]);
        
        // Summary
        rows.push(['Summary Of Measurement']);
        rows.push(['Period', 'Systolic Avg', 'Systolic Min', 'Systolic Max', 'Diastolic Avg', 'Diastolic Min', 'Diastolic Max', 'Pulse Avg', 'Pulse Min', 'Pulse Max', 'Above Target', 'Within Target', 'Below Target']);
        
        periodList.forEach((periodName) => {
          const stats = calculatePeriodStats(effectiveMeasurements, periodName);
          const fmt = (v) => (v != null && !Number.isNaN(v) ? Math.round(v) : '');
          rows.push([
            periodName,
            fmt(stats.avg?.sys), fmt(stats.min?.sys), fmt(stats.max?.sys),
            fmt(stats.avg?.dia), fmt(stats.min?.dia), fmt(stats.max?.dia),
            fmt(stats.avg?.pulse), fmt(stats.min?.pulse), fmt(stats.max?.pulse),
            stats.above || 0, stats.within || 0, stats.below || 0
          ]);
        });
        
        const csvString = toCsv(rows);
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const csvFilename = `BP-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        downloadBlob(blob, csvFilename);
        toast.success('Exported to CSV');
        patientService.logPatientExport(practiceId, patientId, { format: 'csv', source: 'Blood Pressure' });
      } catch (e) {
        toast.error(e?.message || 'Export failed');
      } finally {
        setIsExporting(false);
      }
    }
  };

  const defaultEmailSubject = `Blood Pressure Report - ${patientName} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `Blood Pressure Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${effectiveMeasurements.length}`;

  return (
    <div className="blood-pressure-page">
      {!useSharedControls && practiceIsLocked && (
        <div className="practice-locked-measurement-banner" role="status">
          This practice is locked. Historical readings remain available for up to 30 days. New measurements and
          follow-ups are not allowed.
        </div>
      )}
      <div id="bp-report-content" style={{ padding: '0px' }}>
        {!useSharedControls && (
        <div className="bp-section bp-section--controls">
          <div className="bp-controls-header">
            <div className="bp-controls-header-left">
              <h2 className="bp-section-title">Measurement Controls</h2>
              <div className="bp-patient-chips">
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Name</span>
                    <span className="chip-value">{patientName}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">DOB</span>
                    <span className="chip-value">{patientDOB}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Phone</span>
                    <span className="chip-value">{patientPhone}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Device</span>
                    <span className="chip-value">{patientDevice}</span>
                  </div>
                </div>
              </div>
            </div>
            {!practiceIsLocked && (
              <div className="bp-controls-header-right" data-export-ignore="true">
                <div className="bp-timer-block">
                  <div className="bp-timer-text">
                    <span className="bp-timer-label">Active Session</span>
                    <span className="bp-timer-value">{formatTimer(timer)}</span>
                  </div>
                  <div className="bp-timer-actions">
                    <button
                      type="button"
                      className={`bp-timer-btn bp-timer-toggle ${isRunning ? 'running' : 'paused'}`}
                      onClick={() => setIsRunning((prev) => !prev)}
                      title={isRunning ? 'Pause Timer' : 'Start Timer'}
                      aria-label={isRunning ? 'Pause Timer' : 'Start Timer'}
                    >
                      <FontAwesomeIcon icon={isRunning ? faPause : faPlay} />
                    </button>
                    <button
                      type="button"
                      className="bp-timer-btn bp-timer-close"
                      onClick={() => {
                        setIsRunning(false);
                        setTimer(0);
                      }}
                      title="Reset Timer"
                      aria-label="Reset Timer"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="bp-controls-nav" data-export-ignore="true">
            <VitalsTabs
              practiceId={practiceId}
              patientId={patientId}
              vitals={patientVitals}
              active="blood-pressure"
              mode="measurement"
              className="bp-controls-vitals-tabs"
            />
            {!practiceIsLocked && (
              <button
                className="bp-add-followup-btn"
                type="button"
                onClick={() => {
                  setIsRunning(false);
                  setShowFollowUpModal(true);
                }}
              >
                Add Follow Up
              </button>
            )}
          </div>
        </div>
        )}

        <div className="bp-section bp-section--query" data-export-ignore="true">
          <div className="bp-section-title-row">
            <h2 className="bp-section-title">Query Period</h2>
          </div>
          <div className="bp-card">
          <div className="bp-card-body">
            <div className="bp-query-body">
              <div>
                <label className={`bp-option-card${queryType === 'dateRange' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="bp-queryType"
                    value="dateRange"
                    checked={queryType === 'dateRange'}
                    onChange={() => handleQueryTypeChange('dateRange')}
                  />
                  <span className="bp-option-label">Date Range</span>
                  <select
                    className="bp-dropdown"
                    value={dateRange}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateRange(value);
                      if (String(value).toUpperCase() === 'ALL') {
                        setFromDate('');
                        setToDate('');
                      }
                    }}
                    disabled={queryType !== 'dateRange'}
                  >
                    <option>Last 30 days</option>
                    <option>Last 7 days</option>
                    <option>Last 14 days</option>
                    <option>Last 60 days</option>
                    <option>Last 90 days</option>
                    <option>All</option>
                  </select>
                </label>
                <label className={`bp-option-card${queryType === 'from' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="bp-queryType"
                    value="from"
                    checked={queryType === 'from'}
                    onChange={() => handleQueryTypeChange('from')}
                  />
                  <span className="bp-option-label">From / To</span>
                  <div className="bp-date-inputs" onClick={(e) => e.stopPropagation()}>
                    <AppDateInput
                      className="bp-date-input"
                      value={fromDate}
                      max={getMeasurementQueryMaxDate()}
                      onChange={(nextFrom) => {
                        const next = applyMeasurementFromDateChange(nextFrom, toDate);
                        setFromDate(next.fromDate);
                        setToDate(next.toDate);
                      }}
                      disabled={queryType !== 'from'}
                    />
                    <span className="bp-date-separator">to</span>
                    <AppDateInput
                      className="bp-date-input"
                      value={toDate}
                      min={fromDate || undefined}
                      max={getMeasurementQueryMaxDate()}
                      onChange={(nextTo) => setToDate(sanitizeMeasurementToDate(nextTo, fromDate))}
                      disabled={queryType !== 'from'}
                    />
                  </div>
                </label>
              </div>
              <div>
                <div className="bp-sort-block">
                  <label htmlFor="bp-sort-select">Sort Data By</label>
                  <select
                    id="bp-sort-select"
                    className="bp-dropdown"
                    style={{ width: '100%' }}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option>Weekly average</option>
                    <option>Daily average</option>
                    <option>Monthly average</option>
                    <option>Date</option>
                  </select>
                </div>
                <div className="bp-action-buttons-row">
                  <button className="bp-action-btn primary" type="button" onClick={handleQuery} disabled={isQueryDisabled}>
                    Query
                  </button>
                  <button className="bp-action-btn" type="button" onClick={handleReset} disabled={isResetDisabled}>
                    Reset
                  </button>
                  {!practiceIsLocked && (
                    <button
                      className={`bp-action-btn${!canAddThisVital ? ' bp-action-btn--muted' : ''}`}
                      type="button"
                      disabled={!canAddThisVital}
                      title={!canAddThisVital ? PRACTICE_VITAL_ADD_DISABLED_TITLE : undefined}
                      onClick={() => {
                        if (!canAddThisVital) return;
                        setShowAddModal(true);
                      }}
                    >
                      Add Data
                    </button>
                  )}
                  <button className="bp-action-btn" type="button" onClick={handlePrint}>
                    Print
                  </button>
                  <div className="bp-export-container">
                    <button
                      className="bp-action-btn"
                      type="button"
                      disabled={isExporting}
                      onClick={() => setShowExportMenu((prev) => !prev)}
                      aria-haspopup="menu"
                      aria-expanded={showExportMenu}
                    >
                      {isExporting ? 'Exporting...' : 'Export'} <span className="bp-export-caret">▾</span>
                    </button>
                    <div className={`bp-export-menu${showExportMenu ? ' show' : ''}`} role="menu">
                      <button type="button" role="menuitem" onClick={() => handleExportSubmit('pdf')} disabled={isExporting}>
                        PDF
                      </button>
                      <button type="button" role="menuitem" onClick={() => handleExportSubmit('csv')} disabled={isExporting}>
                        CSV Spreadsheet
                      </button>
                    </div>
                  </div>
                  <button className="bp-action-btn" type="button" onClick={() => setShowEmailModal(true)}>
                    Email
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="bp-section">
          <div className="bp-section-title-row">
            <h2 className="bp-section-title">Blood Pressure Measurements</h2>
            {dateRangeLabel && (
              <span className="bp-section-meta">{dateRangeLabel} · click a cell to see every reading</span>
            )}
          </div>
          <div className="bp-card bp-card--table bp-interactive-only">
          <div className="bp-card-body bp-card-body--flush">
            {queryLoading || loading ? (
              <div className="bp-empty-state">
                <p className="bp-empty-title">Loading measurements…</p>
              </div>
            ) : effectiveMeasurements.length === 0 ? (
              <div className="bp-empty-state">
                <p className="bp-empty-title">No measurement data</p>
                <p className="bp-empty-sub">No measurement data available for the selected period.</p>
              </div>
            ) : (
              <div className="bphr-grid-container">
                <table className="bphr-table">
                  <colgroup>
                    <col style={{ width: '100px' }} />
                    {periodList.map((p) => (
                      <React.Fragment key={p}>
                        <col style={{ width: '95px' }} />
                        <col style={{ width: '95px' }} />
                      </React.Fragment>
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th rowSpan={2} className="datehd">Date</th>
                      {periodList.map((periodName) => {
                        const periodInfo = getPeriodInfo(periodName);
                        const timeRange =
                          periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                            ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
                            : '';
                        return (
                          <th key={periodName} colSpan={2} className="grp">
                            {periodName}
                            {timeRange ? <span className="hds">{timeRange}</span> : null}
                          </th>
                        );
                      })}
                    </tr>
                    <tr>
                      {periodList.map((periodName) => (
                        <React.Fragment key={`${periodName}-sub`}>
                          <th className="sub">
                            BP <span className="sub-unit">(mmHg)</span>
                          </th>
                          <th className="sub">
                            PULSE <span className="sub-unit">(bpm)</span>
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDates.map((date) => {
                      const byPeriod = getDatePeriodMap(date);
                      return (
                        <tr key={date} className="reading-row">
                          <td className="cell date-col">
                            {formatDate(date)}
                          </td>
                          {periodList.map((periodName) => {
                            const readings = sortReadingsNewestFirst(byPeriod[periodName] || []);

                            if (!readings.length) {
                              return (
                                <React.Fragment key={periodName}>
                                  <td className="cell metric"><span className="empty">—</span></td>
                                  <td className="cell metric"><span className="empty">—</span></td>
                                </React.Fragment>
                              );
                            }

                            return (
                              <React.Fragment key={periodName}>
                                <td className="cell metric">
                                  {readings.map((r, idx) => {
                                    const bpStatuses = getReadingBpStatuses(r, periodName);
                                    const sys = r.systolic_pressure != null ? Math.round(Number(r.systolic_pressure)) : '—';
                                    const dia = r.diastolic_pressure != null ? Math.round(Number(r.diastolic_pressure)) : '—';
                                    const sysClass = getReadingLevelClass(bpStatuses.sysStatus);
                                    const diaClass = getReadingLevelClass(bpStatuses.diaStatus);
                                    const timeStr = getReadingTime(r);

                                    return (
                                      <React.Fragment key={r.id || idx}>
                                        {idx > 0 && <div className="dash" />}
                                        <div className="bphr-metric">
                                          <div className="bphr-metric-value">
                                            <span className={sysClass}>{sys}</span>
                                            <span style={{ color: "#64748b", margin: "0 1px" }}>/</span>
                                            <span className={diaClass}>{dia}</span>
                                          </div>
                                          {timeStr ? <div className="bphr-metric-label">{timeStr}</div> : null}
                                        </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </td>
                                <td className="cell metric">
                                  {readings.map((r, idx) => {
                                    const pulseStatus = getReadingPulseStatus(r, periodName);
                                    const pulseVal = r.pulse != null && r.pulse !== '' ? Math.round(Number(r.pulse)) : null;
                                    const pulseClass = getReadingLevelClass(pulseStatus);
                                    const isAbnormalPulse = pulseStatus === 'high' || pulseStatus === 'low';
                                    const timeStr = getReadingTime(r);

                                    return (
                                      <React.Fragment key={r.id || idx}>
                                        {idx > 0 && <div className="dash" />}
                                        <div className="bphr-metric">
                                          <div className="bphr-metric-value">
                                            {pulseVal != null ? (
                                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                                                <span className={pulseClass}>{pulseVal}</span>
                                                <PulseIcon isAbnormal={isAbnormalPulse} size={12} />
                                              </span>
                                            ) : (
                                              <span className="empty">—</span>
                                            )}
                                          </div>
                                          {timeStr ? <div className="bphr-metric-label bphr-metric-spacer" aria-hidden="true">&nbsp;</div> : null}
                                        </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
        </div>

        <div className="bp-section">
          <div className="bp-section-title-row">
            <h2 className="bp-section-title">Summary Of Measurement</h2>
            <div className="bp-target-legend">
              <span><span className="dot above" />Above</span>
              <span><span className="dot within" />Within</span>
              <span><span className="dot below" />Below</span>
            </div>
          </div>
          <div className="bp-card bp-card--table">
          <div className="bp-card-body bp-card-body--flush">
            {queryLoading ? (
              <div className="bp-empty-state"><p className="bp-empty-title">Loading summary…</p></div>
            ) : (
              <>
                <table className="bp-simple-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="num">Systolic (avg / max / min)</th>
                      <th className="num">Diastolic (avg / max / min)</th>
                      <th className="num">Pulse (avg / max / min)</th>
                      <th>Target Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodList.map((periodName) => {
                      const periodInfo = getPeriodInfo(periodName);
                      const timeRange =
                        periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                          ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
                          : '';
                      const stats = calculatePeriodStats(effectiveMeasurements, periodName);
                      const totalSeg = (stats.above || 0) + (stats.within || 0) + (stats.below || 0);
                      const fmt = (v) => (v != null && !Number.isNaN(v) ? Math.round(v) : '—');
                      const hasData = (stats.count || 0) > 0;
                      return (
                        <tr key={periodName}>
                          <td>
                            <span className={`bp-period-badge ${getPeriodBadgeClass(periodName)}`}>
                              <span className="pb-name">{periodName}</span>
                              {timeRange ? <span className="pb-time">{timeRange}</span> : null}
                            </span>
                          </td>
                          {hasData ? (
                            <>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg?.sys)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min?.sys)}–{fmt(stats.max?.sys)})</span>
                                </div>
                              </td>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg?.dia)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min?.dia)}–{fmt(stats.max?.dia)})</span>
                                </div>
                              </td>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg?.pulse)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min?.pulse)}–{fmt(stats.max?.pulse)})</span>
                                </div>
                              </td>
                            </>
                          ) : (
                            <td className="num bp-no-data-inline" colSpan={3}>No readings</td>
                          )}
                          <td className="bp-target-bar-cell">
                            <div className="bp-target-seg-bar">
                              {totalSeg > 0 ? (
                                <>
                                  <div className="seg above" style={{ width: `${(stats.above / totalSeg) * 100}%` }} />
                                  <div className="seg within" style={{ width: `${(stats.within / totalSeg) * 100}%` }} />
                                  <div className="seg below" style={{ width: `${(stats.below / totalSeg) * 100}%` }} />
                                </>
                              ) : (
                                <div className="seg" style={{ width: '100%', background: '#F1F0EC' }} />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="bp-total-grid">
                  <div className="bp-total-card above">
                    <div className="bp-total-title">Above target</div>
                    <div className="stat-line">Systolic: {formatPercentCount(totals.above.sys, totals.sysTotal)}</div>
                    <div className="stat-line">Diastolic: {formatPercentCount(totals.above.dia, totals.diaTotal)}</div>
                  </div>
                  <div className="bp-total-card within">
                    <div className="bp-total-title">Within target</div>
                    <div className="stat-line">Systolic: {formatPercentCount(totals.within.sys, totals.sysTotal)}</div>
                    <div className="stat-line">Diastolic: {formatPercentCount(totals.within.dia, totals.diaTotal)}</div>
                  </div>
                  <div className="bp-total-card below">
                    <div className="bp-total-title">Below target</div>
                    <div className="stat-line">Systolic: {formatPercentCount(totals.below.sys, totals.sysTotal)}</div>
                    <div className="stat-line">Diastolic: {formatPercentCount(totals.below.dia, totals.diaTotal)}</div>
                  </div>
                </div>
              </>
            )}
          </div>
          </div>
        </div>

        {!practiceIsLocked && canAddThisVital && showAddModal && (
          <AddBPForm
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            practiceId={practiceId}
            patientId={patientId}
            initialScheduleTargets={scheduleTargets}
            onSuccess={handleAddDataSuccess}
            modalClassName="bp-add-data-modal"
          />
        )}

        {!useSharedControls && !practiceIsLocked && showFollowUpModal && (
          <AddFollowUpModal
            isOpen={showFollowUpModal}
            onClose={() => setShowFollowUpModal(false)}
            practiceId={practiceId}
            patientId={patientId}
            patientName={patientName}
            initialTimerValue={timer}
            onTimerSync={(seconds) => {
              setTimer(Math.max(0, Number(seconds) || 0));
              setIsRunning(true);
            }}
            onSuccess={() => { }}
          />
        )}
      </div>

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={async (emailData) => {
          try {
            setEmailSending(true);
            const res = await sendReportEmail(emailData, { event_id: 9 });
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            patientService.logPatientExport(practiceId, patientId, {
              format: 'email',
              source: 'Blood Pressure'
            });
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          } finally {
            setEmailSending(false);
          }
        }}
      />
    </div>
  );
};

export default BloodPressure;
